import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Trash2, Loader2 } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  adminService,
  UserDownload as UserDownloadType,
} from "../../services/adminService";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/errorHandler";

const formatBrowser = (name?: string | null, ver?: string | null) =>
  [name, ver].filter(Boolean).join(" ").trim() || "—";
const formatOs = (name?: string | null, ver?: string | null) =>
  [name, ver].filter(Boolean).join(" ").trim() || "—";

const UserDownload = () => {
  const [downloads, setDownloads] = useState<UserDownloadType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });

  const fetchDownloads = useCallback(
    async (page = 1, search = "") => {
      try {
        setLoading(true);
        const data = await adminService.getUserDownloads({
          page: page,
          page_size: pagination.pageSize,
          search: search,
          ordering: "Id", // Order by Id ASC as requested
        });
        setDownloads(data.results);
        setPagination(prev => ({
          ...prev,
          count: data.count,
          page: page,
        }));
      } catch (error: any) {
        console.error("Error fetching downloads:", error);
        showErrorToast(
          error,
          "Failed to load download history. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) {
      return;
    }
    fetchedRef.current = true;
    fetchDownloads();
    return () => {
      fetchedRef.current = false;
    };
  }, [fetchDownloads]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchDownloads(1, searchTerm);
  };

  const handleDelete = async (id: number) => {
    if (
      !window.confirm("Are you sure you want to delete this download record?")
    ) {
      return;
    }
    try {
      await adminService.deleteUserDownload(id);
      toast.success("Download record deleted successfully");
      fetchDownloads(pagination.page, searchTerm);
    } catch (error: any) {
      console.error("Error deleting download record:", error);
      showErrorToast(error, "Failed to delete record");
    }
  };

  const totalPages = Math.ceil(pagination.count / pagination.pageSize);

  return (
    <AdminLayout>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <h2 className="text-xl font-bold text-foreground md:text-3xl">
            User Download History
          </h2>
          <form
            onSubmit={handleSearch}
            className="flex w-full max-w-sm items-center gap-2"
          >
            <div className="relative w-full">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search by user, file, IP, browser, UA…"
                className="w-full pl-9"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <Button
              type="submit"
              variant="secondary"
              className="shrink-0 bg-[#533a1d] text-white hover:bg-[#533a1d]/90"
            >
              Search
            </Button>
          </form>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="w-16 px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    UID
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Username
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Book SR No
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Filename
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Count
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    IP (captured)
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Country
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    City
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    State
                  </th>
                  <th className="min-w-[100px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Browser
                  </th>
                  <th className="min-w-[100px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    OS
                  </th>
                  <th className="w-20 px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Device
                  </th>
                  <th className="max-w-[140px] px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    UA
                  </th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider text-muted-foreground">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading && downloads.length === 0 ? (
                  <tr>
                    <td colSpan={16} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-8 w-8 animate-spin" />
                        <p>Loading download history...</p>
                      </div>
                    </td>
                  </tr>
                ) : downloads.length === 0 ? (
                  <tr>
                    <td
                      colSpan={16}
                      className="px-4 py-12 text-center text-muted-foreground"
                    >
                      No download records found.
                    </td>
                  </tr>
                ) : (
                  downloads.map(item => (
                    <tr
                      key={item.id}
                      className="transition-colors hover:bg-muted/30"
                    >
                      <td className="px-4 py-3 font-medium">{item.id}</td>
                      <td className="px-4 py-3">{item.uid ?? "-"}</td>
                      <td className="px-4 py-3">{item.username}</td>
                      <td className="px-4 py-3">
                        {item.book_sr_no ?? item.sr_no}
                      </td>
                      <td
                        className="max-w-xs truncate px-4 py-3"
                        title={item.filename}
                      >
                        {item.filename}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {new Date(item.ddate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-center">{item.count}</td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {item.ip_address ?? item.ipaddr ?? "—"}
                      </td>
                      <td className="px-4 py-3">{item.country ?? "—"}</td>
                      <td className="max-w-[120px] truncate px-4 py-3">
                        {item.city ?? "—"}
                      </td>
                      <td className="max-w-[100px] truncate px-4 py-3">
                        {item.state ?? "—"}
                      </td>
                      <td
                        className="max-w-[130px] truncate px-4 py-3 text-xs"
                        title={formatBrowser(
                          item.browser_name,
                          item.browser_version
                        )}
                      >
                        {formatBrowser(item.browser_name, item.browser_version)}
                      </td>
                      <td
                        className="max-w-[130px] truncate px-4 py-3 text-xs"
                        title={formatOs(item.os_name, item.os_version)}
                      >
                        {formatOs(item.os_name, item.os_version)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs capitalize">
                        {item.device_type ?? "—"}
                      </td>
                      <td
                        className="max-w-[160px] truncate px-4 py-3 font-mono text-[10px]"
                        title={item.user_agent ?? undefined}
                      >
                        {item.user_agent
                          ? `${item.user_agent.slice(0, 48)}${
                              item.user_agent.length > 48 ? "…" : ""
                            }`
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:bg-destructive/80"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <div className="text-sm text-muted-foreground">
            {pagination.count > 0 ? (
              <>
                Showing{" "}
                <span className="font-medium text-foreground">
                  {(pagination.page - 1) * pagination.pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium text-foreground">
                  {Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.count
                  )}
                </span>{" "}
                of{" "}
                <span className="font-medium text-foreground">
                  {pagination.count}
                </span>{" "}
                results
              </>
            ) : (
              "No results to show"
            )}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchDownloads(1, searchTerm)}
              disabled={pagination.page === 1 || loading}
            >
              « First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchDownloads(pagination.page - 1, searchTerm)}
              disabled={pagination.page === 1 || loading}
            >
              ‹ Back
            </Button>
            <div className="flex h-9 items-center rounded-md border bg-muted/50 px-4 text-sm font-medium">
              Page {pagination.page} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchDownloads(pagination.page + 1, searchTerm)}
              disabled={pagination.page >= totalPages || loading}
            >
              Next ›
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchDownloads(totalPages, searchTerm)}
              disabled={pagination.page >= totalPages || loading}
            >
              Last »
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserDownload;
